/**
 * User Service
 * Handles user data fetching
 * Mock/Real switching is handled at Service layer
 */

import { IS_MOCK, MOCK_DELAY } from '../../constants/config';
import { apiService } from '../api/ApiService';
import { mockUsers, generateUserDetail } from '../mock/mockUsers';
import type { UserStatus, UserType } from '../../types/user';
import { delay } from '../mock/mockData';
import { parseDate, formatDate, formatDateTime } from '../../utils/dateUtils';
import { getCallSignDisplayName } from '../../utils/generalUtils';
import type {
  User,
  UserFilter,
  PaginatedUsers,
  UserDetail,
  PaymentMethod,
  UserWarning,
} from '../../types/user';

class UserService {
  private static getUserDisplayName(user: {
    callSign?: string | null;
    role?: string | null;
    userType?: string | null;
  }): string {
    const normalizedUserType = (user.role || user.userType || '').toLowerCase();

    if (normalizedUserType === 'asset') {
      return getCallSignDisplayName(user.callSign, 'Asset');
    }

    if (normalizedUserType === 'ghost') {
      return getCallSignDisplayName(user.callSign, 'Ghost');
    }

    return getCallSignDisplayName(user.callSign, 'User');
  }

  private static sanitizeMockUser(user: User): User {
    const fallbackLabel = user.userType === 'asset' ? 'Asset' : 'Ghost';

    return {
      ...user,
      name: getCallSignDisplayName(user.name, fallbackLabel),
    };
  }

  private static sanitizeMockUserDetail(userDetail: UserDetail): UserDetail {
    const fallbackLabel = userDetail.userType === 'asset' ? 'Asset' : 'Ghost';

    return {
      ...userDetail,
      name: getCallSignDisplayName(userDetail.name, fallbackLabel),
    };
  }

  /**
   * Convert API user format to internal User format
   */
  private static convertUserFromApi(apiUser: any): User {
    return {
      id: apiUser.id,
      userId: apiUser.userId || apiUser.id,
      name: UserService.getUserDisplayName(apiUser),
      email: apiUser.email,
      status: apiUser.status.toLowerCase() as UserStatus,
      userType: apiUser.role.toLowerCase() as UserType,
      avatar: apiUser.avatarUrl,
      lastLogin: apiUser.lastLoginAt ? (parseDate(apiUser.lastLoginAt) ?? undefined) : undefined,
      lastLoginText: apiUser.lastLoginAt ? formatDateTime(apiUser.lastLoginAt) : '',
      createdAt: apiUser.createdAt ? (parseDate(apiUser.createdAt) ?? undefined) : undefined,
      createdAtText: apiUser.createdAt ? formatDate(apiUser.createdAt) : '',
    };
  }

  /**
   * Apply shared detail fields returned by the base user API.
   */
  private static applyCommonDetailFields(userDetail: UserDetail, apiUser: any): UserDetail {
    if (apiUser.totalMissions !== undefined) {
      userDetail.totalMissions = apiUser.totalMissions;
    }
    if (apiUser.completedMissions !== undefined) {
      userDetail.completedMissions = apiUser.completedMissions;
    }
    if (apiUser.paymentMethods && Array.isArray(apiUser.paymentMethods)) {
      userDetail.paymentMethods = apiUser.paymentMethods.map(
        (pm: any) => UserService.convertPaymentMethodFromApi(pm)
      );
    }

    return userDetail;
  }

  /**
   * Get paginated users with optional filters
   */
  static async getUsers(
    page: number = 1,
    pageSize: number = 10,
    filter?: UserFilter
  ): Promise<PaginatedUsers> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);

      let filteredUsers = mockUsers.map(UserService.sanitizeMockUser);

      // Apply status filter
      if (filter?.status && filter.status !== 'all') {
        filteredUsers = filteredUsers.filter(
          (user) => user.status === filter.status
        );
      }

      // Apply user type filter
      if (filter?.userType && filter.userType !== 'all') {
        filteredUsers = filteredUsers.filter(
          (user) => user.userType === filter.userType
        );
      }

      // Apply search filter
      if (filter?.search) {
        const searchLower = filter.search.toLowerCase();
        filteredUsers = filteredUsers.filter(
          (user) =>
            user.name.toLowerCase().includes(searchLower) ||
            user.email.toLowerCase().includes(searchLower) ||
            user.userId.toLowerCase().includes(searchLower)
        );
      }

      const total = filteredUsers.length;
      const totalPages = Math.ceil(total / pageSize);
      const startIndex = (page - 1) * pageSize;
      const data = filteredUsers.slice(startIndex, startIndex + pageSize);

      return {
        data,
        total,
        page,
        pageSize,
        totalPages,
      };
    } else {
      const params: Record<string, string> = {
        page: page.toString(),
        pageSize: pageSize.toString(),
      };

      // Add optional filters
      if (filter?.status && filter.status !== 'all') {
        params.status = filter.status;
      }
      if (filter?.userType && filter.userType !== 'all') {
        params.userType = filter.userType;
      }
      if (filter?.search) {
        params.keyword = filter.search;
      }

      const response = await apiService.get<{
        isSuccess: boolean;
        data: {
          total: number;
          items: User[];
        };
        message?: string;
      }>('/admin/users', { params });

      if (!response.isSuccess || !response.data) {
        throw new Error(response.message || 'Failed to fetch users');
      }

      // Convert API response to expected format
      return {
        data: response.data.items.map(UserService.convertUserFromApi),
        total: response.data.total,
        page,
        pageSize,
        totalPages: Math.ceil(response.data.total / pageSize),
      };
    }
  }

  /**
   * Get user by ID
   */
  static async getUserById(id: string): Promise<User | null> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      const user = mockUsers.find(
        (item) =>
          item.id === id ||
          item.userId === id
      );

      return user ? UserService.sanitizeMockUser(user) : null;
    } else {
      const response = await apiService.get<{
        isSuccess: boolean;
        data: User;
        message?: string;
      }>(`/admin/users/${id}`);

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to fetch user');
      }

      return response.data ? UserService.convertUserFromApi(response.data) : null;
    }
  }

  /**
   * Convert API PaymentMethodData to frontend PaymentMethod format
   */
  private static convertPaymentMethodFromApi(apiMethod: any): PaymentMethod {
    const typeMap: Record<string, 'credit_card' | 'debit_card' | 'bank_account'> = {
      'Card': 'credit_card',
      'card': 'credit_card',
      'CreditCard': 'credit_card',
      'credit_card': 'credit_card',
      'DebitCard': 'debit_card',
      'debit_card': 'debit_card',
      'Ach': 'bank_account',
      'bank_account': 'bank_account',
    };

    const rawType = apiMethod.type || apiMethod.Type;
    const type = typeMap[rawType] || 'credit_card';

    return {
      id: apiMethod.id || apiMethod.Id || '',
      type: type,
      last4: apiMethod.last4 || apiMethod.Last4 || undefined,
      brand: apiMethod.brand || apiMethod.Brand || undefined,
      expiryMonth: apiMethod.expiryMonth !== undefined
        ? (typeof apiMethod.expiryMonth === 'string' ? parseInt(apiMethod.expiryMonth, 10) : apiMethod.expiryMonth)
        : (apiMethod.expMonth ? parseInt(apiMethod.expMonth, 10) : undefined),
      expiryYear: apiMethod.expiryYear !== undefined
        ? (typeof apiMethod.expiryYear === 'string' ? parseInt(apiMethod.expiryYear, 10) : apiMethod.expiryYear)
        : (apiMethod.expYear ? parseInt(apiMethod.expYear, 10) : undefined),
      isDefault: apiMethod.isDefault !== undefined ? apiMethod.isDefault : (apiMethod.IsDefault || false),
      holderName: apiMethod.holderName || apiMethod.HolderName || apiMethod.cardHolderName || apiMethod.CardHolderName || undefined,
    };
  }

  /**
   * Get user detail by ID
   */
  static async getUserDetail(id: string): Promise<UserDetail | null> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      const userDetail = generateUserDetail(id);
      return userDetail ? UserService.sanitizeMockUserDetail(userDetail) : null;
    } else {
      const response = await apiService.get<{
        isSuccess: boolean;
        data: any;
        message?: string;
      }>(`/admin/users/${id}`);

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to fetch user detail');
      }

      if (!response.data) {
        return null;
      }

      const userDetail = UserService.applyCommonDetailFields(
        UserService.convertUserFromApi(response.data) as UserDetail,
        response.data
      ) as any;

      if (response.data.role?.toLowerCase() === 'ghost') {
        const ghostResponse = await apiService.get<{
          isSuccess: boolean;
          data: any;
        }>(`/admin/ghosts/${id}`);

        if (ghostResponse.isSuccess && ghostResponse.data) {
          const gd = ghostResponse.data;
          // Ghost API returns empty warnings[], so always use warnings from UserManagement API
          return {
            ...userDetail,
            phone: gd.phone,
            personalInfo: gd.personalInfo,
            license: gd.license,
            skills: gd.skills,
            performanceMetrics: gd.performanceMetrics,
            earnings: gd.earnings,
            tier: gd.tier,
            tasks: gd.tasks || [],
            payments: gd.payments || [],
            activities: gd.activities || [],
            isVerified: gd.isVerified,
            bondInfo: gd.bondInfo ? {
              bondNumber: gd.license?.licenseNumber || gd.bondInfo.bondNumber,
              bondCompanyName: gd.bondInfo.bondCompanyName,
              naicNumber: gd.bondInfo.naicNumber,
              bondAmount: gd.bondInfo.insuredAmount,
              bondExpiryDate: gd.bondInfo.bondExpirationDate ? parseDate(gd.bondInfo.bondExpirationDate) || undefined : undefined,
              bondExpiryDateText: gd.bondInfo.bondExpirationDate ? formatDate(gd.bondInfo.bondExpirationDate) : undefined,
              bondDocument: gd.bondInfo.bondDocumentUrl ? {
                id: 'bond-doc',
                url: gd.bondInfo.bondDocumentUrl,
                fileName: gd.bondInfo.bondDocumentName || 'bond-document.pdf',
                uploadedAt: new Date(),
                uploadedAtText: formatDateTime(new Date()),
                mimeType: 'application/octet-stream'
              } : undefined
            } : undefined
          };
        }
      }

      return userDetail;
    }
  }

  /**
   * Get user warning history
   */
  static async getWarningHistory(
    id: string,
    keyword?: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<{ data: UserWarning[]; total: number }> {
    if (IS_MOCK) {
      // Mock logic can be kept empty or return dummy data
      return { data: [], total: 0 };
    } else {
      let url = `/admin/users/${id}/warnings?page=${page}&pageSize=${pageSize}`;
      if (keyword) {
        url += `&keyword=${encodeURIComponent(keyword)}`;
      }

      const response = await apiService.get<any>(url);

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to fetch warning history');
      }

      if (!response.data || !response.data.items) {
        return { data: [], total: 0 };
      }

      const warnings = response.data.items.map((w: any) => ({
        id: w.id.toString(),
        issuedBy: w.issuedByAdminId,
        issuedByName: w.issuedByName,
        reason: w.reason,
        date: parseDate(w.date) ?? undefined,
      }));

      return {
        data: warnings,
        total: response.data.totalCount || 0
      };
    }
  }

  /**
   * Suspend a user
   */
  static async suspendUser(id: string, suspendedUntil: string, reason: string): Promise<void> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      // Update mock user status
      const user = mockUsers.find((u) => u.id === id || u.userId === id);
      if (user) {
        user.status = 'suspended';
      }
      return;
    } else {
      const response = await apiService.post<{
        isSuccess: boolean;
        message?: string;
      }>(`/admin/users/${id}/suspend`, {
        suspendedUntil,
        reason,
      });

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to suspend user');
      }
    }
  }

  /**
   * Activate a suspended user
   */
  static async activateUser(id: string, reason: string): Promise<void> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      // Update mock user status
      const user = mockUsers.find((u) => u.id === id || u.userId === id);
      if (user) {
        user.status = 'active';
      }
      return;
    } else {
      const response = await apiService.post<{
        isSuccess: boolean;
        message?: string;
      }>(`/admin/users/${id}/activate`, {
        reason,
      });

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to activate user');
      }
    }
  }

  /**
   * Issue a warning to a user
   */
  static async issueWarning(id: string, reason: string): Promise<void> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      // In mock, we don't have a persistent state for warnings per user session,
      // but we return success to simulate the API call.
      return;
    } else {
      const response = await apiService.post<{
        isSuccess: boolean;
        message?: string;
      }>(`/admin/users/${id}/warnings`, {
        reason,
      });

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to issue warning');
      }
    }
  }

  /**
   * Get current user profile
   */
  static async getCurrentUser(): Promise<{
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    address: string | null;
    avatarUrl: string | null;
  }> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);

      // Return mock current user data
      return {
        id: 'ef2c670d-5fdd-44e4-90b3-6ac1e8b7a716',
        email: 'admin@mispy.ai',
        firstName: 'Seeded',
        lastName: 'Admin',
        address: null,
        avatarUrl: null
      };
    } else {
      const response = await apiService.get<{
        isSuccess: boolean;
        data: {
          id: string;
          email: string;
          firstName: string;
          lastName: string;
          address: string | null;
          avatarUrl: string | null;
        };
        message: string | null;
      }>('/users/me');

      if (!response.isSuccess || !response.data) {
        throw new Error(response.message || 'Failed to fetch current user');
      }

      return response.data;
    }
  }

  /**
   * Get user payment methods by user ID (Admin only)
   * Uses the Admin API endpoint: GET /admin/users/{userId}/payment-methods
   */
  static async getUserPaymentMethods(userId: string): Promise<PaymentMethod[]> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      // Return mock payment methods
      const userDetail = generateUserDetail(userId);
      return userDetail?.paymentMethods || [];
    } else {
      try {
        // Call Admin API endpoint to get user's payment methods
        const response = await apiService.get<{
          isSuccess: boolean;
          data: any[] | any;
          message?: string;
        }>(`/admin/users/${userId}/payment-methods`);

        if (response.isSuccess && response.data) {
          const methods = Array.isArray(response.data) ? response.data : [response.data];
          return methods.map(UserService.convertPaymentMethodFromApi);
        }

        return [];
      } catch (error: any) {
        console.error(`Failed to fetch payment methods for user ${userId}:`, error);
        // Return empty array on error instead of throwing
        return [];
      }
    }
  }

  /**
   * Get user payment transactions (Admin only)
   * Uses the Admin API endpoint: GET /admin/users/{userId}/payments
   */
  static async getUserPayments(
    userId: string,
    page: number = 1,
    pageSize: number = 20,
    keyword?: string
  ): Promise<{
    data: Array<{
      id: string;
      missionId: string;
      missionCode: string;
      missionTitle: string;
      amount: number;
      platformFee: number;
      bookingFee: number;
      ghostEarnings: number;
      currency: string;
      status: string;
      operationType: string;
      transactionAt: string | null;
    }>;
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      // Return empty mock data
      return {
        data: [],
        total: 0,
        page,
        pageSize,
        totalPages: 0,
      };
    } else {
      try {
        const params: Record<string, string> = {
          page: page.toString(),
          pageSize: pageSize.toString(),
        };
        if (keyword) {
          params.keyword = keyword;
        }

        const response = await apiService.get<{
          isSuccess: boolean;
          data: {
            total: number;
            items: any[];
          };
          message?: string;
        }>(`/admin/users/${userId}/payments`, { params });

        if (!response.isSuccess || !response.data) {
          throw new Error(response.message || 'Failed to fetch user payments');
        }

        const items = response.data.items || [];
        const total = response.data.total || 0;

        return {
          data: items.map((item: any) => ({
            id: item.id || item.Id || '',
            missionId: item.missionId || item.MissionId || '',
            missionCode: item.missionCode || item.MissionCode || '',
            missionTitle: item.missionTitle || item.MissionTitle || '',
            amount: Number(item.amount ?? item.Amount ?? 0),
            platformFee: Number(item.platformFee ?? item.PlatformFee ?? 0),
            bookingFee: Number(item.bookingFee ?? item.BookingFee ?? 0),
            ghostEarnings: Number(item.ghostEarnings ?? item.GhostEarnings ?? 0),
            currency: item.currency || item.Currency || 'USD',
            status: item.status || item.Status || '',
            operationType: item.operationType || item.OperationType || '',
            transactionAt: item.transactionAt || item.TransactionAt || null,
          })),
          total,
          page,
          pageSize,
          totalPages: Math.ceil(total / pageSize),
        };
      } catch (error: any) {
        console.error(`Failed to fetch payments for user ${userId}:`, error);
        return {
          data: [],
          total: 0,
          page,
          pageSize,
          totalPages: 0,
        };
      }
    }
  }
}

export { UserService };
